import Box from "../../Box";
import { BUTTON_ROW_NAMES, UI_TEXT } from "../constants";
import type { CalculationMode } from "../types";
import CalculatorButton from "./CalculatorButton";

interface ButtonConfig {
	title: string;
	onPress: () => void;
	variant?: "orange" | "default";
	textSize?: "small" | "default";
}

interface ButtonGridProps {
	onModeChange: (mode: CalculationMode) => void;
	onNavigationPress: (direction: "up" | "down") => void;
	onOperatorPress: (op: string) => void;
	onNumberPress: (num: string) => void;
	onMathOperation: (op: string) => void;
	onMemoryOperations: (op: string) => void;
	onTimer: () => void;
	onCalculation: () => void;
	onUnitOperations?: (op: string) => void;
}

const ButtonGrid = ({
	onModeChange,
	onNavigationPress,
	onOperatorPress,
	onNumberPress,
	onMathOperation,
	onMemoryOperations,
	onTimer,
	onCalculation,
	onUnitOperations,
}: ButtonGridProps) => {
	const buttonRows: ButtonConfig[][] = [
		[
			{
				title: UI_TEXT.BUTTONS.FLT,
				onPress: () => onModeChange("unit_conversions"),
			},
			{
				title: UI_TEXT.BUTTONS.PLAN,
				onPress: () => onModeChange("speed_time_distance"),
			},
			{
				title: UI_TEXT.BUTTONS.UP_ARROW,
				onPress: () => onNavigationPress("up"),
			},
			{ title: UI_TEXT.BUTTONS.TIMER, onPress: onTimer },
			{ title: UI_TEXT.BUTTONS.CALC, onPress: onCalculation },
		],
		[
			{ title: UI_TEXT.BUTTONS.BACK, onPress: () => onOperatorPress("BACK") },
			{ title: UI_TEXT.BUTTONS.STAR, onPress: () => onMemoryOperations("M+") },
			{
				title: UI_TEXT.BUTTONS.WIND,
				onPress: () => onModeChange("wind_triangle"),
				variant: "orange" as const,
			},
			{
				title: UI_TEXT.BUTTONS.WEIGHT_BALANCE,
				onPress: () => onModeChange("weight_balance"),
			},
			{ title: UI_TEXT.BUTTONS.SET, onPress: () => onMemoryOperations("MC") },
		],
		[
			{ title: UI_TEXT.BUTTONS.MEMORY, onPress: () => onMemoryOperations("M") },
			{
				title: UI_TEXT.BUTTONS.SET_UNIT,
				onPress: () => onUnitOperations?.("SET_UNIT"),
				textSize: "small" as const,
			},
			{
				title: UI_TEXT.BUTTONS.DOWN_ARROW,
				onPress: () => onNavigationPress("down"),
			},
			{
				title: UI_TEXT.BUTTONS.CONV_UNIT,
				onPress: () => onUnitOperations?.("CONV_UNIT"),
				textSize: "small" as const,
			},
			{ title: UI_TEXT.BUTTONS.DIVIDE, onPress: () => onMathOperation("÷") },
		],
		[
			{ title: UI_TEXT.BUTTONS.CLEAR, onPress: () => onOperatorPress("C") },
			{ title: UI_TEXT.BUTTONS.SEVEN, onPress: () => onNumberPress("7") },
			{ title: UI_TEXT.BUTTONS.EIGHT, onPress: () => onNumberPress("8") },
			{ title: UI_TEXT.BUTTONS.NINE, onPress: () => onNumberPress("9") },
			{ title: UI_TEXT.BUTTONS.MULTIPLY, onPress: () => onMathOperation("×") },
		],
		[
			{
				title: UI_TEXT.BUTTONS.BACKSPACE,
				onPress: () => onOperatorPress("BKSP"),
				textSize: "small" as const,
			},
			{ title: UI_TEXT.BUTTONS.FOUR, onPress: () => onNumberPress("4") },
			{ title: UI_TEXT.BUTTONS.FIVE, onPress: () => onNumberPress("5") },
			{ title: UI_TEXT.BUTTONS.SIX, onPress: () => onNumberPress("6") },
			{ title: UI_TEXT.BUTTONS.MINUS, onPress: () => onMathOperation("M-") },
		],
		[
			{ title: UI_TEXT.BUTTONS.COLON, onPress: () => onMemoryOperations("M-") },
			{ title: UI_TEXT.BUTTONS.ONE, onPress: () => onNumberPress("1") },
			{ title: UI_TEXT.BUTTONS.TWO, onPress: () => onNumberPress("2") },
			{ title: UI_TEXT.BUTTONS.THREE, onPress: () => onNumberPress("3") },
			{ title: UI_TEXT.BUTTONS.PLUS, onPress: () => onMathOperation("+") },
		],
		[
			{
				title: UI_TEXT.BUTTONS.PLUS_MINUS,
				onPress: () => onOperatorPress("+/-"),
			},
			{ title: UI_TEXT.BUTTONS.DOT, onPress: () => onOperatorPress(".") },
			{ title: UI_TEXT.BUTTONS.ZERO, onPress: () => onNumberPress("0") },
			{ title: UI_TEXT.BUTTONS.CHECKMARK, onPress: onCalculation },
			{
				title: UI_TEXT.BUTTONS.EQUALS,
				onPress: () => onOperatorPress("="),
				variant: "orange" as const,
			},
		],
	];

	return (
		<Box
			style={{
				flex: 1,
				padding: 2,
				gap: 2,
			}}
		>
			{buttonRows.map((row, rowIndex) => (
				<Box
					key={BUTTON_ROW_NAMES[rowIndex]}
					style={{
						flexDirection: "row",
						flex: 1,
						minHeight: 42,
					}}
				>
					{row.map((button) => (
						<CalculatorButton
							key={`r${rowIndex}-${button.title.replace(/\n/g, "-")}`}
							title={button.title}
							onPress={button.onPress}
							variant={button.variant}
						/>
					))}
				</Box>
			))}
		</Box>
	);
};

export default ButtonGrid;
